import axios, {
    type AxiosInstance,
    type AxiosRequestConfig,
    type AxiosResponse,
    type AxiosError,
    type InternalAxiosRequestConfig
} from 'axios';

export type RetryOptions = {
    maxRetries?: number;
    retryDelay?: number;
    maxRetryDelay?: number;
    retryAnyError?: boolean;
};

/**
 * Slightly modified front-end version of http-client.ts in serverless app
 */
class HttpClient {
    private client: AxiosInstance;

    private maxRetries: number;
    private retryDelay: number;
    private maxRetryDelay: number;
    private retryAnyError: boolean = false;

    static RETRYABLE_STATUS_CODES: number[] = [
        408, // Request Timeout
        425, // Too Early
        429, // Too Many Requests
        500, // Internal Server Error
        502, // Bad Gateway
        503, // Service Unavailable
        504 // Gateway Timeout
    ];

    constructor(config: AxiosRequestConfig = {}, retryOptions: RetryOptions = {}) {
        const { maxRetries = 6, retryDelay = 100, maxRetryDelay = 4000, retryAnyError = false } = retryOptions;

        this.maxRetries = maxRetries;
        this.retryDelay = retryDelay;
        this.maxRetryDelay = maxRetryDelay;
        this.retryAnyError = retryAnyError;

        if (config.timeout === undefined) {
            config.timeout = 15000;
        }

        this.client = axios.create(config);
    }

    /**
     * Set a header to use across all requests
     * for this instance.
     */
    public setHeader(name: string, value: string): void {
        this.client.defaults.headers.common[name] = value;
    }

    /**
     * Add a request interceptor
     */
    public addRequestInterceptor(
        onFulfilled?: (
            value: InternalAxiosRequestConfig
        ) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>,
        onRejected?: (error: any) => any
    ): void {
        this.client.interceptors.request.use(onFulfilled, onRejected);
    }

    /**
     * Add a response interceptor
     */
    public addResponseInterceptor(
        onFulfilled?: (value: AxiosResponse) => AxiosResponse | Promise<AxiosResponse>,
        onRejected?: (error: any) => any
    ): void {
        this.client.interceptors.response.use(onFulfilled, onRejected);
    }

    public async get<T = any>(url: string, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> {
        return this.requestWithRetry<T>(() => this.client.get<T>(url, config));
    }

    public async post<T = any>(
        url: string,
        data: any = {},
        config: AxiosRequestConfig = {}
    ): Promise<AxiosResponse<T>> {
        return this.requestWithRetry<T>(() => this.client.post<T>(url, data, config));
    }

    public async put<T = any>(url: string, data: any = {}, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> {
        return this.requestWithRetry<T>(() => this.client.put<T>(url, data, config));
    }

    public async patch<T = any>(
        url: string,
        data: any = {},
        config: AxiosRequestConfig = {}
    ): Promise<AxiosResponse<T>> {
        return this.requestWithRetry<T>(() => this.client.patch<T>(url, data, config));
    }

    public async delete<T = any>(url: string, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> {
        return this.requestWithRetry<T>(() => this.client.delete<T>(url, config));
    }

    public getMaxRetries(): number {
        return this.maxRetries;
    }

    public setMaxRetries(value: number) {
        this.maxRetries = value;
    }

    public getRetryDelay(): number {
        return this.retryDelay;
    }

    public setRetryDelay(value: number) {
        this.retryDelay = value;
    }

    public getMaxRetryDelay(): number {
        return this.maxRetryDelay;
    }

    public setMaxRetryDelay(value: number) {
        this.maxRetryDelay = value;
    }

    /**
     * Perform an initial request and subsequent retries on
     * valid retryable response status codes.
     */
    private async requestWithRetry<T = any>(
        requestFn: () => Promise<AxiosResponse<T>>,
        retryAttempt: number = 0
    ): Promise<AxiosResponse<T>> {
        try {
            const response = await requestFn();

            return response;
        } catch (error) {
            const statusCode = (error as AxiosError).response?.status;

            if (!this.shouldRetry(retryAttempt, statusCode!)) {
                throw error;
            }

            const retryDelay = this.calculateRetryDelay(retryAttempt);
            await this.delay(retryDelay);

            return this.requestWithRetry<T>(requestFn, retryAttempt + 1);
        }
    }

    /**
     * Determines whether a request should be retried after a failure.
     */
    private shouldRetry(retryAttempt: number, statusCode: number): boolean {
        if (retryAttempt >= this.maxRetries) {
            return false;
        }
        if (this.retryAnyError) {
            return true;
        }
        if (!HttpClient.RETRYABLE_STATUS_CODES.includes(statusCode)) {
            return false;
        }

        return true;
    }

    /**
     * Calculate the retry delay on a request with exponential
     * backoff and jitter.
     */
    private calculateRetryDelay(retryCount: number): number {
        const exponentialBackoffDelay = this.retryDelay * 2 ** retryCount;
        const jitter = Math.random() * (exponentialBackoffDelay / 2);

        const delay = exponentialBackoffDelay + jitter;

        return Math.min(delay, this.maxRetryDelay);
    }

    // eslint-disable-next-line class-methods-use-this
    private delay(ms: number): Promise<void> {
        return new Promise((r) => setTimeout(r, ms));
    }
}

export default HttpClient;
